// Positions from Keplerian elements with secular rates.
//
// The method is JPL's "Approximate Positions of the Planets" (Standish and
// Williams 1992, https://ssd.jpl.nasa.gov/planets/approx_pos.html): advance the
// six elements at their per-century rates, add the b, c, s and f terms to the
// mean anomaly for Jupiter through Pluto, solve Kepler's equation, then rotate
// the orbital-plane vector into the J2000 ecliptic. Stated accuracy is a few
// arcminutes as seen from Earth over 3000 BC to 3000 AD, which is about one
// pixel at the Atlas field of view (docs/roadmap/01-world-model.md).
//
// Pure by construction: the one runtime import is the shared angle helper
// below, itself leaf-simple TS, so tools/preview can still run this under
// Node with types stripped. A row with an osculating sidecar (satellites,
// the tracked asteroids, Jupiter and Saturn) blends the two bracketing
// samples with cubic Hermite splines instead of the six-element secular
// formula above; the per-frame sweep (src/sim/bodies.ts, src/atlas/orrery.ts)
// additionally has out-parameters so it can fill the same RETURNED object
// every tick instead of allocating one - the sidecar join itself still builds
// a handful of short-lived objects of its own per call (a synthetic orbit
// shell, one value/rate pair per blended element), about 2 to 2.5x a secular
// row's own cost, since only the caller-visible result is out-parameter'd.
// The parent-index, synthetic-orbit and barycentre partner-node caches below
// are the one exception to "no state": memoization keyed on the registry's own
// row and orbit objects, never observable from a result, and never
// invalidated because the registry loads once and its rows are never replaced
// in place.

import type { BodyRow, Orbit, OrbitLaplace, OrbitSets } from './types.ts';
import { wrap180 } from '../math/angles.ts';
import { TEST_SWITCHES } from '../urlFlags.ts';
import { GM_EARTH_MOON_KM3_S2, lunarStateAt } from './lunar.ts';

export const AU_KM = 149597870.7;
export const DAYS_PER_CENTURY = 36525;
const SECONDS_PER_CENTURY = DAYS_PER_CENTURY * 86400;
const DEG = Math.PI / 180;

// Heliocentric gravitational parameter, DE430/DE440 (JPL Horizons "Sun"
// physical/dynamical constants, GM). Every osculating set in the sidecar
// today is heliocentric, so tools/atlas/sample_elements.mjs writes this same
// value into each set's own gm_km3_s2 rather than stateAt assuming it; it is
// exported so that tool can import it instead of duplicating the literal.
export const GM_SUN_KM3_S2 = 1.32712440018e11;
const SECONDS_PER_DAY = 86400;

/** Two-body mean motion in degrees per day for a semi-major axis about a
 * centre of the given gravitational parameter. */
function meanMotionDegPerDay(aAu: number, gmKm3S2: number): number {
  const aKm = aAu * AU_KM;
  const nRadPerSec = Math.sqrt(gmKm3S2 / (aKm * aKm * aKm));
  return (nRadPerSec * SECONDS_PER_DAY) / DEG;
}

/**
 * `?ephemnearest=1`: every sampled row propagates from its nearest set alone,
 * so the position steps where the nearest set changes, the way it did before
 * the join below. `ephem-check.mjs --inject nearest` reaches it, in Node and in
 * the page; nothing a player presses does.
 */
const NEAREST_SET_ONLY = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).get('ephemnearest') === '1';

/**
 * `?ephemnorate=1`: the joined orbit's mean-motion rate drops the weight's own
 * time derivative again, the way it read before that term was added, so the
 * returned velocity goes back to lagging the returned position's own rate.
 * `ephem-check.mjs --inject no-rate-term` reaches it; nothing a player presses
 * does.
 */
const NO_RATE_TERM = typeof location !== 'undefined' && TEST_SWITCHES
  && new URLSearchParams(location.search).get('ephemnorate') === '1';

/** Index of the last sample at or before jdTdb, for epochs[0] <= jdTdb < epochs[last]. */
function bracketSetIndex(epochs: readonly number[], jdTdb: number): number {
  let lo = 0;
  let hi = epochs.length - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (epochs[mid]! <= jdTdb) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Index of the sample nearest jdTdb in an ascending epoch array. */
function nearestSetIndex(epochs: readonly number[], jdTdb: number): number {
  const last = epochs.length - 1;
  if (jdTdb <= epochs[0]!) return 0;
  if (jdTdb >= epochs[last]!) return last;
  const lo = bracketSetIndex(epochs, jdTdb);
  return jdTdb - epochs[lo]! <= epochs[lo + 1]! - jdTdb ? lo : lo + 1;
}

/**
 * True where `orbit.sets` both exists and covers jdTdb, within half a sample
 * spacing of either end. Exported so tooling can report which path a given
 * epoch actually took; `stateAt` uses the same test to choose its branch.
 */
export function usesOsculatingSet(orbit: Orbit, jdTdb: number): boolean {
  const sets = orbit.sets;
  if (sets === undefined) return false;
  const spacing = orbit.osculating?.spacing_days ?? 0;
  const first = sets.epoch_jd[0];
  const last = sets.epoch_jd[sets.epoch_jd.length - 1];
  if (first === undefined || last === undefined) return false;
  return jdTdb >= first - spacing / 2 && jdTdb <= last + spacing / 2;
}

export interface Vec3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** Ecliptic J2000 state: position in km, velocity in km/s. */
export interface State {
  readonly position: Vec3;
  readonly velocity: Vec3;
}

/** Position in km, velocity in km/s, filled in place by an `out` parameter. */
interface MutableVec3 { x: number; y: number; z: number; }
interface MutableState { position: MutableVec3; velocity: MutableVec3; }

function newVec3(): MutableVec3 { return { x: 0, y: 0, z: 0 }; }
function newState(): MutableState { return { position: newVec3(), velocity: newVec3() }; }

/**
 * Eccentric anomaly in radians from mean anomaly in radians. Newton on
 * M = E - e sin E from the JPL starting guess; the table's own tolerance is
 * 1e-6 degrees, and this holds 1e-12 radians in a handful of steps.
 */
export function kepler(meanAnomalyRad: number, e: number): number {
  let E = meanAnomalyRad + e * Math.sin(meanAnomalyRad);
  for (let i = 0; i < 24; i++) {
    const dM = meanAnomalyRad - (E - e * Math.sin(E));
    const dE = dM / (1 - e * Math.cos(E));
    E += dE;
    if (Math.abs(dE) < 1e-12) break;
  }
  return E;
}

/**
 * The elements one epoch actually resolved to, and the rotation they imply.
 *
 * `stateAt` used to hold these in locals. They are handed out so that a caller
 * drawing the WHOLE ellipse - the orbit rings in `src/atlas/orrery.ts` - walks
 * the same numbers the body was placed from rather than a second evaluation of
 * its own, which is what keeps the body on its own ring to the bit.
 */
export interface OrbitElements {
  /** Semi-major axis, AU. */
  readonly a: number;
  readonly e: number;
  readonly iRad: number;
  readonly nodeRad: number;
  /** Longitude of perihelion, radians. */
  readonly periRad: number;
  /** Argument of perihelion (perihelion minus node), radians. */
  readonly wRad: number;
  /** Mean anomaly, radians, wrapped the way the solver wants it. */
  readonly mRad: number;
  /** The eccentric anomaly this epoch solved to, radians. */
  readonly E: number;
  /** sqrt(1 - e^2), the semi-minor axis as a share of `a`. */
  readonly flat: number;
  /** dE/dt in radians per second at `E`, for the velocity. */
  readonly dEdt: number;
  /** Orbital plane into ecliptic J2000, the two columns a conic needs. */
  readonly m11: number; readonly m12: number;
  readonly m21: number; readonly m22: number;
  readonly m31: number; readonly m32: number;
}

/** `OrbitElements`, writable: what an `out` parameter is filled through. */
type MutableElements = { -readonly [K in keyof OrbitElements]: OrbitElements[K] };

function newElements(): MutableElements {
  return {
    a: 0, e: 0, iRad: 0, nodeRad: 0, periRad: 0, wRad: 0, mRad: 0, E: 0, flat: 0, dEdt: 0,
    m11: 0, m12: 0, m21: 0, m22: 0, m31: 0, m32: 0,
  };
}

/**
 * `Orbit`, writable: the shape the osculating branch's synthetic set reuses.
 * Spelled out field by field rather than mapped, since a mapped type over
 * `Orbit` intersected with a mutable `rates` leaves `rates` itself ambiguous
 * about whether it is still readonly.
 */
interface MutableOrbit {
  epoch: Orbit['epoch'];
  epoch_jd: Orbit['epoch_jd'];
  frame: Orbit['frame'];
  parent_relative: Orbit['parent_relative'];
  a_au: Orbit['a_au'];
  e: Orbit['e'];
  i_deg: Orbit['i_deg'];
  L_deg: Orbit['L_deg'];
  long_peri_deg: Orbit['long_peri_deg'];
  long_node_deg: Orbit['long_node_deg'];
  rates: {
    a_au_cy: Orbit['rates']['a_au_cy'];
    e_cy: Orbit['rates']['e_cy'];
    i_deg_cy: Orbit['rates']['i_deg_cy'];
    L_deg_cy: Orbit['rates']['L_deg_cy'];
    long_peri_deg_cy: Orbit['rates']['long_peri_deg_cy'];
    long_node_deg_cy: Orbit['rates']['long_node_deg_cy'];
  };
  extra_terms: Orbit['extra_terms'];
  rates_source: Orbit['rates_source'];
}

// One synthetic single-epoch orbit per row that carries a `sets` sidecar,
// keyed on the row's own orbit object (stable for the registry's lifetime).
// `elementsAt` used to allocate this fresh - the shell plus its own `rates`
// object - on every call; now the shell is built once and only the six
// elements, the epoch and the mean-motion rate are overwritten per call.
const SYNTHETIC_ORBIT_CACHE = new WeakMap<Orbit, MutableOrbit>();

function syntheticShell(orbit: Orbit): MutableOrbit {
  let synthetic = SYNTHETIC_ORBIT_CACHE.get(orbit);
  if (synthetic === undefined) {
    synthetic = {
      epoch: 'osculating',
      epoch_jd: 0,
      frame: 'ecliptic_j2000',
      parent_relative: orbit.parent_relative,
      a_au: 0, e: 0, i_deg: 0, L_deg: 0, long_peri_deg: 0, long_node_deg: 0,
      rates: { a_au_cy: 0, e_cy: 0, i_deg_cy: 0, L_deg_cy: 0, long_peri_deg_cy: 0, long_node_deg_cy: 0 },
      extra_terms: null,
      rates_source: 'osculating_two_body',
    };
    SYNTHETIC_ORBIT_CACHE.set(orbit, synthetic);
  }
  return synthetic;
}

function syntheticOsculatingOrbit(orbit: Orbit, idx: number, a: number, sets: OrbitSets): MutableOrbit {
  const synthetic = syntheticShell(orbit);
  synthetic.epoch_jd = sets.epoch_jd[idx]!;
  synthetic.a_au = a;
  synthetic.e = sets.e[idx]!;
  synthetic.i_deg = sets.i_deg[idx]!;
  synthetic.L_deg = sets.L_deg[idx]!;
  synthetic.long_peri_deg = sets.long_peri_deg[idx]!;
  synthetic.long_node_deg = sets.long_node_deg[idx]!;
  synthetic.rates.L_deg_cy = meanMotionDegPerDay(a, sets.gm_km3_s2) * DAYS_PER_CENTURY;
  return synthetic;
}

/** An angle difference in degrees taken the short way round; `wrap180`'s `%` costs more per call. */
function shortTurnDeg(deg: number): number {
  return deg - 360 * Math.round(deg / 360);
}

/**
 * Cubic Hermite value and per-day rate from `p0` at `w=0` to `p1` at `w=1`,
 * over an interval `dtDays` long, given each end's own per-day tangent.
 * Matches both value AND slope at each end, which piecewise-linear does not -
 * that mismatch is what let the old join's exact-derivative velocity jump at
 * every sample boundary even though the position beneath it looked smooth.
 *
 * `w` outside [0, 1] happens where `usesOsculatingSet`'s own half-spacing
 * margin runs past the sidecar's first or last sample: there the cubic terms
 * grow fast (Phobos's 2450 fixture, one day past its last set, is w=1.25 on a
 * 4-day interval) and can overshoot the true motion by more than holding flat
 * did - Phobos's own worst velocity miss there went 28.0 -> 63.5 m/s under a
 * straight cubic extrapolation. A straight line at the edge's own tangent
 * matches this same curve in value and rate exactly at w=0 or w=1 (h10d(0)=1
 * and h11d(1)=1, every other basis derivative there is 0) and does not run
 * away past it.
 */
function hermite(p0: number, p1: number, m0: number, m1: number, dtDays: number, w: number): { value: number; ratePerDay: number } {
  if (w <= 0) return { value: p0 + m0 * dtDays * w, ratePerDay: m0 };
  if (w >= 1) return { value: p1 + m1 * dtDays * (w - 1), ratePerDay: m1 };
  const w2 = w * w;
  const w3 = w2 * w;
  const h00 = 2 * w3 - 3 * w2 + 1;
  const h10 = w3 - 2 * w2 + w;
  const h01 = -2 * w3 + 3 * w2;
  const h11 = w3 - w2;
  const value = h00 * p0 + h10 * dtDays * m0 + h01 * p1 + h11 * dtDays * m1;
  const h00d = 6 * w2 - 6 * w;
  const h10d = 3 * w2 - 4 * w + 1;
  const h01d = -6 * w2 + 6 * w;
  const h11d = 3 * w2 - 2 * w;
  const ratePerDay = (h00d * p0 + h10d * dtDays * m0 + h01d * p1 + h11d * dtDays * m1) / dtDays;
  return { value, ratePerDay };
}

/** One interval's own raw slope between two sampled columns, deg/day; wrap-safe for an angle. */
function intervalRateDeg(col: readonly number[], epochs: readonly number[], i: number, j: number, angle: boolean): number {
  const dv = angle ? shortTurnDeg(col[j]! - col[i]!) : col[j]! - col[i]!;
  return dv / (epochs[j]! - epochs[i]!);
}

/**
 * True where no set after sample `k` may be joined to it: `k` is the last
 * sample, or the sampler marked a close pass by a planet (`encounter_jd`)
 * inside the interval that follows it. A pass changes the orbit within days,
 * so the sets either side describe two different orbits and a blend of them
 * matches neither.
 */
function segmentEndsAt(sets: OrbitSets, k: number): boolean {
  const epochs = sets.epoch_jd;
  if (k >= epochs.length - 1) return true;
  const passes = sets.encounter_jd;
  if (passes === undefined) return false;
  const t0 = epochs[k]!;
  const t1 = epochs[k + 1]!;
  for (let i = 0; i < passes.length; i++) {
    const t = passes[i]!;
    if (t > t0 && t < t1) return true;
  }
  return false;
}

/**
 * Inside an interval holding a marked pass, the sample on `jdTdb`'s own side
 * of it; -1 for an interval that holds none.
 */
function passSideIndex(sets: OrbitSets, lo: number, jdTdb: number): number {
  if (lo + 1 >= sets.epoch_jd.length || !segmentEndsAt(sets, lo)) return -1;
  const passes = sets.encounter_jd!;
  const t0 = sets.epoch_jd[lo]!;
  const t1 = sets.epoch_jd[lo + 1]!;
  for (let i = 0; i < passes.length; i++) {
    const t = passes[i]!;
    if (t > t0 && t < t1) return jdTdb < t ? lo : lo + 1;
  }
  return -1;
}

/**
 * The tangent at sample `k`, shared by the interval before it and the one
 * after, so a Hermite blend on either side agrees at every knot: the end
 * interval's own one-sided secant at the first and last sample of a run of
 * sets that may be joined (there is no neighbour on the far side to average
 * against, and 2000-01-01 and Bennu's 2135-09-30 are both reachable from the
 * Atlas era slider, so a zero tangent there is a real, played approximation
 * rather than an instrument artefact - `elementsAt` no longer holds a, e, i, node and peri
 * fixed past either end, it keeps drifting at this same rate, which is what
 * lets `jointRatesAt` stay defined there too instead of handing off to a
 * separate fixed-element velocity formula with a different, silent zero); the
 * average of the two neighbouring intervals' own slopes elsewhere. A run of
 * one sample, a set with a marked pass on both sides, has no slope to take.
 */
function sampleTangentDeg(sets: OrbitSets, col: readonly number[], k: number, angle: boolean): number {
  const epochs = sets.epoch_jd;
  const first = k === 0 || segmentEndsAt(sets, k - 1);
  const last = segmentEndsAt(sets, k);
  if (first && last) return 0;
  if (first) return intervalRateDeg(col, epochs, k, k + 1, angle);
  if (last) return intervalRateDeg(col, epochs, k - 1, k, angle);
  return 0.5 * (intervalRateDeg(col, epochs, k - 1, k, angle) + intervalRateDeg(col, epochs, k, k + 1, angle));
}

/** A sampled column's Hermite value and rate at `jdTdb`, bracketed by `lo`/`hi`. */
function hermiteColumnAt(
  sets: OrbitSets, col: readonly number[], lo: number, hi: number, jdTdb: number, angle: boolean,
): { value: number; ratePerDay: number } {
  const epochs = sets.epoch_jd;
  const tLo = epochs[lo]!;
  const tHi = epochs[hi]!;
  const dt = tHi - tLo;
  const w = (jdTdb - tLo) / dt;
  const p0 = col[lo]!;
  const p1 = angle ? p0 + shortTurnDeg(col[hi]! - p0) : col[hi]!;
  const m0 = sampleTangentDeg(sets, col, lo, angle);
  const m1 = sampleTangentDeg(sets, col, hi, angle);
  return hermite(p0, p1, m0, m1, dt, w);
}

/**
 * A sampled column at `jdTdb` in the interval starting at `lo`: the Hermite
 * join, or across a marked pass the set on the date's own side alone, carried
 * at its own tangent - the same straight line the join runs on past the
 * sidecar's own first and last sample.
 */
function sampledColumnAt(
  sets: OrbitSets, col: readonly number[], lo: number, jdTdb: number, angle: boolean,
): { value: number; ratePerDay: number } {
  const k = passSideIndex(sets, lo, jdTdb);
  if (k < 0) return hermiteColumnAt(sets, col, lo, lo + 1, jdTdb, angle);
  const m = sampleTangentDeg(sets, col, k, angle);
  return { value: col[k]! + m * (jdTdb - sets.epoch_jd[k]!), ratePerDay: m };
}

/**
 * One interval's own two-body-anchored rate for L, deg/day: the sample at `i`
 * advanced by its own mean motion, plus whatever extra turn (the short way,
 * safe for a fast mover like Phobos crossing 360 degrees many times over one
 * interval) lands exactly on the sample at `j`.
 */
function intervalLongitudeRateDeg(sets: OrbitSets, i: number, j: number): number {
  const dt = sets.epoch_jd[j]! - sets.epoch_jd[i]!;
  const nI = meanMotionDegPerDay(sets.a_au[i]!, sets.gm_km3_s2);
  const turn = shortTurnDeg(sets.L_deg[j]! - (sets.L_deg[i]! + nI * dt));
  return nI + turn / dt;
}

/**
 * The L rate shared by the interval before sample `k` and the one after, so
 * a Hermite blend of L on either side agrees there: pure two-body (matching
 * the single-epoch branch just past it) at the first and last sample of a run
 * of sets that may be joined, the average of the two neighbouring intervals'
 * own anchored rates elsewhere. Unlike the other five elements, the edge
 * tangent here is NOT zero - the single-epoch branch still advances L by that sample's own mean
 * motion, it just holds a, e, i, node and peri fixed.
 */
function longitudeTangentDeg(sets: OrbitSets, k: number): number {
  if (k === 0 || segmentEndsAt(sets, k - 1) || segmentEndsAt(sets, k)) {
    return meanMotionDegPerDay(sets.a_au[k]!, sets.gm_km3_s2);
  }
  return 0.5 * (intervalLongitudeRateDeg(sets, k - 1, k) + intervalLongitudeRateDeg(sets, k, k + 1));
}

/**
 * Mean longitude at `jdTdb`, Hermite-blended from `lo` to an unwrapped target
 * anchored on two-body propagation from `lo` (so a fast mover's many-turn
 * value never gets lost to a plain angle wrap), using the shared tangents
 * above so the result is C1 at every sample - including the L rate folded
 * into the tangent itself, not held out as a separate additive term the way
 * an earlier version of this function did, since that term was evaluated
 * from whichever interval's own left sample was current and so did not
 * agree between the two intervals meeting at one.
 */
function joinedLongitudeAt(sets: OrbitSets, lo: number, hi: number, jdTdb: number): { value: number; ratePerDay: number } {
  const k = passSideIndex(sets, lo, jdTdb);
  if (k >= 0) {
    const n = longitudeTangentDeg(sets, k);
    return { value: sets.L_deg[k]! + n * (jdTdb - sets.epoch_jd[k]!), ratePerDay: n };
  }
  const tLo = sets.epoch_jd[lo]!;
  const tHi = sets.epoch_jd[hi]!;
  const dt = tHi - tLo;
  const w = (jdTdb - tLo) / dt;
  const p0 = sets.L_deg[lo]!;
  const p1 = p0 + intervalLongitudeRateDeg(sets, lo, hi) * dt;
  const m0 = longitudeTangentDeg(sets, lo);
  const m1 = longitudeTangentDeg(sets, hi);
  return hermite(p0, p1, m0, m1, dt, w);
}

/**
 * The two sets either side of jdTdb joined into one, weighted so each holds
 * exactly at its own epoch and nothing steps between them - and, since
 * `--rate` requires the returned velocity to be the exact derivative of the
 * returned position, joined with cubic Hermite splines rather than a plain
 * linear blend: piecewise-linear matches value at each sample but not slope,
 * so its exact derivative has a genuine kink there, which is what `--boundaries`
 * caught. A shared per-sample tangent (`sampleTangentDeg`) gives both intervals
 * touching a sample the identical rate, so the Hermite result is continuous in
 * both value and derivative everywhere `--boundaries` checks.
 *
 * The mean longitude keeps its own two-body-plus-residual construction
 * (`joinedLongitudeAt`) rather than a raw Hermite blend of L itself, since
 * that is what cancels the mean-motion error both sets share - for Phobos,
 * most of the perturbation two-body leaves out.
 */
function joinedOsculatingOrbit(orbit: Orbit, sets: OrbitSets, jdTdb: number): MutableOrbit {
  const lo = bracketSetIndex(sets.epoch_jd, jdTdb);
  const hi = lo + 1;
  const epochs = sets.epoch_jd;
  const synthetic = syntheticShell(orbit);
  synthetic.epoch_jd = jdTdb;
  synthetic.a_au = sampledColumnAt(sets, sets.a_au, lo, jdTdb, false).value;
  synthetic.e = sampledColumnAt(sets, sets.e, lo, jdTdb, false).value;
  synthetic.i_deg = sampledColumnAt(sets, sets.i_deg, lo, jdTdb, false).value;
  synthetic.long_peri_deg = sampledColumnAt(sets, sets.long_peri_deg, lo, jdTdb, true).value;
  synthetic.long_node_deg = sampledColumnAt(sets, sets.long_node_deg, lo, jdTdb, true).value;
  const l = joinedLongitudeAt(sets, lo, hi, jdTdb);
  synthetic.L_deg = l.value;
  if (NO_RATE_TERM) {
    const w = (jdTdb - epochs[lo]!) / (epochs[hi]! - epochs[lo]!);
    const nLo = meanMotionDegPerDay(sets.a_au[lo]!, sets.gm_km3_s2);
    const nHi = meanMotionDegPerDay(sets.a_au[hi]!, sets.gm_km3_s2);
    synthetic.rates.L_deg_cy = (nLo + w * (nHi - nLo)) * DAYS_PER_CENTURY;
  } else {
    synthetic.rates.L_deg_cy = l.ratePerDay * DAYS_PER_CENTURY;
  }
  return synthetic;
}

/** The five extra element rates the Hermite join gives a, e, i, node and peri, for `stateAt`'s velocity. */
interface JointRates {
  readonly aDotAuPerSec: number;
  readonly eDotPerSec: number;
  readonly iDotRadPerSec: number;
  readonly nodeDotRadPerSec: number;
  readonly periDotRadPerSec: number;
}

/**
 * `joinedOsculatingOrbit` Hermite-blends a, e, i, node and peri between the
 * same two sets it blends L across, so each carries a rate at `jdTdb` too -
 * the same rate `sampledColumnAt` used to build the position, so position and
 * velocity stay consistent by construction. Null off `usesOsculatingSet`'s own
 * range entirely (the secular fit beyond it, which has no rate of its own for
 * these five) or under `--inject nearest` or `--inject no-rate-term`'s
 * `?ephemnearest=1` / `?ephemnorate=1`, so `stateAt` falls back to the plain
 * fixed-element formula.
 */
function jointRatesAt(orbit: Orbit, jdTdb: number): JointRates | null {
  if (NO_RATE_TERM || NEAREST_SET_ONLY) return null;
  if (!usesOsculatingSet(orbit, jdTdb)) return null;
  const sets = orbit.sets;
  if (sets === undefined) return null;
  const epochs = sets.epoch_jd;
  const lo = bracketSetIndex(epochs, jdTdb);
  const hi = lo + 1;
  const aRate = sampledColumnAt(sets, sets.a_au, lo, jdTdb, false).ratePerDay;
  const eRate = sampledColumnAt(sets, sets.e, lo, jdTdb, false).ratePerDay;
  const iRate = sampledColumnAt(sets, sets.i_deg, lo, jdTdb, false).ratePerDay;
  const nodeRate = sampledColumnAt(sets, sets.long_node_deg, lo, jdTdb, true).ratePerDay;
  const periRate = sampledColumnAt(sets, sets.long_peri_deg, lo, jdTdb, true).ratePerDay;
  return {
    aDotAuPerSec: aRate / SECONDS_PER_DAY,
    eDotPerSec: eRate / SECONDS_PER_DAY,
    iDotRadPerSec: iRate * DEG / SECONDS_PER_DAY,
    nodeDotRadPerSec: nodeRate * DEG / SECONDS_PER_DAY,
    periDotRadPerSec: periRate * DEG / SECONDS_PER_DAY,
  };
}

const THEORY_SCRATCH = newState();
// A frame asks for the Moon twice at one date, and the trip planner often does too.
const THEORY_MEMO = newElements();
let theoryMemoJd = Number.NaN;

/**
 * The osculating orbit through one state about a centre of parameter `gm`, in
 * the same shape the element branches fill: `positionFromE` at its own E
 * returns that state's position, and `stateAt`'s two-body velocity its velocity.
 * The plane's two columns are the periapsis direction and the one 90 degrees
 * ahead of it, read straight off the state rather than rebuilt from angles.
 */
function osculatingFromState(s: State, gm: number, o: MutableElements): OrbitElements {
  const { x, y, z } = s.position;
  const { x: vx, y: vy, z: vz } = s.velocity;
  const r = Math.hypot(x, y, z);
  const v2 = vx * vx + vy * vy + vz * vz;
  const rv = x * vx + y * vy + z * vz;
  const aKm = 1 / (2 / r - v2 / gm);
  const hx = y * vz - z * vy;
  const hy = z * vx - x * vz;
  const hz = x * vy - y * vx;
  const h = Math.hypot(hx, hy, hz);
  const k = v2 - gm / r;
  const ex = (k * x - rv * vx) / gm;
  const ey = (k * y - rv * vy) / gm;
  const ez = (k * z - rv * vz) / gm;
  const e = Math.hypot(ex, ey, ez);
  const px = ex / e; const py = ey / e; const pz = ez / e;
  const E = Math.atan2(rv / Math.sqrt(gm * aKm), 1 - r / aKm);
  o.a = aKm / AU_KM;
  o.e = e;
  o.E = E;
  o.mRad = E - e * Math.sin(E);
  o.flat = Math.sqrt(1 - e * e);
  o.dEdt = Math.sqrt(gm / (aKm * aKm * aKm)) / (1 - e * Math.cos(E));
  o.m11 = px; o.m21 = py; o.m31 = pz;
  o.m12 = (hy * pz - hz * py) / h;
  o.m22 = (hz * px - hx * pz) / h;
  o.m32 = (hx * py - hy * px) / h;
  o.iRad = Math.acos(Math.max(-1, Math.min(1, hz / h)));
  o.nodeRad = Math.atan2(hx, -hy);
  const nx = Math.cos(o.nodeRad);
  const ny = Math.sin(o.nodeRad);
  const bx = -hz * ny / h;
  const by = hz * nx / h;
  const bz = (hx * ny - hy * nx) / h;
  o.wRad = Math.atan2(px * bx + py * by + pz * bz, px * nx + py * ny);
  o.periRad = o.nodeRad + o.wRad;
  return o;
}

/**
 * One element set advanced to a TDB Julian date, with its Kepler solve done.
 * A row whose `orbit.theory` names a lunar theory resolves to the osculating
 * orbit through that theory's own state instead.
 *
 * The osculating branch joins the two sets bracketing jdTdb across the whole
 * of `usesOsculatingSet`'s own range, including the half-spacing margin past
 * either end of the sidecar - the same Hermite curve the strict interior
 * uses, extrapolated a little past its own last real sample rather than
 * switching formula there, so a, e, i, node and peri keep drifting at the end
 * interval's own rate instead of freezing. `--inject nearest` (`NEAREST_SET_ONLY`)
 * recurses once into a synthetic single-epoch orbit with no `sets` instead,
 * so it cannot recurse a second time.
 *
 * `out`, when given, is filled in place and returned instead of a fresh
 * object - the per-frame sweep's own copy, never one this module reuses on
 * its own, so nothing outside a caller's own `out` can see it change.
 */
export function elementsAt(orbit: Orbit, jdTdb: number, out?: MutableElements): OrbitElements {
  const o = out ?? newElements();
  if (orbit.theory === 'meeus_ch47') {
    if (jdTdb !== theoryMemoJd) {
      osculatingFromState(lunarStateAt(jdTdb, THEORY_SCRATCH), GM_EARTH_MOON_KM3_S2, THEORY_MEMO);
      theoryMemoJd = jdTdb;
    }
    return Object.assign(o, THEORY_MEMO);
  }
  if (usesOsculatingSet(orbit, jdTdb)) {
    const sets = orbit.sets!;
    if (NEAREST_SET_ONLY) {
      const idx = nearestSetIndex(sets.epoch_jd, jdTdb);
      return elementsAt(syntheticOsculatingOrbit(orbit, idx, sets.a_au[idx]!, sets), jdTdb, o);
    }
    return elementsAt(joinedOsculatingOrbit(orbit, sets, jdTdb), jdTdb, o);
  }

  const T = (jdTdb - orbit.epoch_jd) / DAYS_PER_CENTURY;
  const r = orbit.rates;

  const a = orbit.a_au + r.a_au_cy * T;
  const e = orbit.e + r.e_cy * T;
  const lp = orbit.laplace;
  const iRad = (lp ? lp.i_deg : orbit.i_deg + r.i_deg_cy * T) * DEG;
  let meanLongDeg = lp ? lp.L_deg + lp.L_deg_cy * T : orbit.L_deg + r.L_deg_cy * T;
  const periDeg = lp ? lp.long_peri_deg + lp.long_peri_deg_cy * T : orbit.long_peri_deg + r.long_peri_deg_cy * T;
  const nodeDeg = lp ? lp.long_node_deg + lp.long_node_deg_cy * T : orbit.long_node_deg + r.long_node_deg_cy * T;
  let lRateDegCy = lp ? lp.L_deg_cy : r.L_deg_cy;
  const terms = lp?.L_terms;
  if (terms !== undefined) {
    for (let k = 0; k < terms.length; k++) {
      const term = terms[k]!;
      const turnsPerCy = DAYS_PER_CENTURY / term.period_days;
      const arg = (360 * turnsPerCy * T + term.phase_deg) * DEG;
      meanLongDeg += term.amplitude_deg * Math.sin(arg);
      lRateDegCy += term.amplitude_deg * Math.cos(arg) * 2 * Math.PI * turnsPerCy;
    }
  }

  let mDeg = meanLongDeg - periDeg;
  const x = orbit.extra_terms;
  if (x !== null) {
    const fT = x.f * T * DEG;
    mDeg += x.b * T * T + x.c * Math.cos(fT) + x.s * Math.sin(fT);
  }
  const mRad = wrap180(mDeg) * DEG;
  const E = kepler(mRad, e);

  const wRad = (periDeg - nodeDeg) * DEG;
  const nRad = nodeDeg * DEG;
  const flat = Math.sqrt(Math.max(0, 1 - e * e));
  // dE/dt, the eccentric anomaly's own rate - not an eccentricity rate,
  // despite the name a variable called "eDot" would suggest.
  const dEdt = (lRateDegCy * DEG) / SECONDS_PER_CENTURY / (1 - e * Math.cos(E));

  const cw = Math.cos(wRad);
  const sw = Math.sin(wRad);
  const cn = Math.cos(nRad);
  const sn = Math.sin(nRad);
  const ci = Math.cos(iRad);
  const si = Math.sin(iRad);

  o.a = a; o.e = e; o.iRad = iRad; o.nodeRad = nRad; o.periRad = periDeg * DEG;
  o.wRad = wRad; o.mRad = mRad; o.E = E; o.flat = flat; o.dEdt = dEdt;
  o.m11 = cw * cn - sw * sn * ci;
  o.m12 = -sw * cn - cw * sn * ci;
  o.m21 = cw * sn + sw * cn * ci;
  o.m22 = -sw * sn + cw * cn * ci;
  o.m31 = sw * si;
  o.m32 = cw * si;
  if (lp) laplaceToEcliptic(lp, o);
  return o;
}

/**
 * Turn elements referred to a row's Laplace plane into the ecliptic: the
 * plane's own axes (x its ascending node on the ecliptic, z its pole) premultiply
 * m, and the angles are read back off the result, since `transfer.ts` takes an
 * orbit's plane from `iRad` and `nodeRad`.
 */
function laplaceToEcliptic(lp: OrbitLaplace, o: MutableElements): void {
  const lon = lp.axis_lon_deg * DEG;
  const lat = lp.axis_lat_deg * DEG;
  const zx = Math.cos(lat) * Math.cos(lon);
  const zy = Math.cos(lat) * Math.sin(lon);
  const zz = Math.sin(lat);
  const xn = Math.hypot(zx, zy);
  const xx = -zy / xn;
  const xy = zx / xn;
  // y = z cross x, with x in the ecliptic plane.
  const yx = -zz * xy;
  const yy = zz * xx;
  const yz = zx * xy - zy * xx;

  const p1 = xx * o.m11 + yx * o.m21 + zx * o.m31;
  const p2 = xy * o.m11 + yy * o.m21 + zy * o.m31;
  const p3 = yz * o.m21 + zz * o.m31;
  const q1 = xx * o.m12 + yx * o.m22 + zx * o.m32;
  const q2 = xy * o.m12 + yy * o.m22 + zy * o.m32;
  const q3 = yz * o.m22 + zz * o.m32;
  o.m11 = p1; o.m21 = p2; o.m31 = p3;
  o.m12 = q1; o.m22 = q2; o.m32 = q3;

  // Orbit normal P x Q, its ascending node, and the arc from that node to periapsis P.
  const hx = p2 * q3 - p3 * q2;
  const hy = p3 * q1 - p1 * q3;
  const hz = p1 * q2 - p2 * q1;
  o.iRad = Math.acos(Math.max(-1, Math.min(1, hz)));
  o.nodeRad = Math.atan2(hx, -hy);
  const nx = Math.cos(o.nodeRad);
  const ny = Math.sin(o.nodeRad);
  // h x n, the in-plane direction 90 degrees ahead of the node.
  const bx = -hz * ny;
  const by = hz * nx;
  const bz = hx * ny - hy * nx;
  o.wRad = Math.atan2(p1 * bx + p2 * by + p3 * bz, p1 * nx + p2 * ny);
  o.periRad = o.nodeRad + o.wRad;
}

/**
 * Where one eccentric anomaly falls, in km, in the frame the elements are in.
 *
 * Two trig calls and a rotation: no Kepler solve, because E is the free
 * variable here rather than the thing being solved for. Sweeping E over a turn
 * is the whole ellipse, and E equal to `el.E` is the body itself.
 */
export function positionFromE(el: OrbitElements, E: number, out?: MutableVec3): Vec3 {
  const cosE = Math.cos(E);
  const sinE = Math.sin(E);
  // Orbital plane, x' from focus to perihelion, in AU.
  const xp = el.a * (cosE - el.e);
  const yp = el.a * el.flat * sinE;
  const o = out ?? newVec3();
  o.x = (el.m11 * xp + el.m12 * yp) * AU_KM;
  o.y = (el.m21 * xp + el.m22 * yp) * AU_KM;
  o.z = (el.m31 * xp + el.m32 * yp) * AU_KM;
  return o;
}

// The `elementsAt` result inside `stateAt` is never handed back to a
// `stateAt` caller - only its derived position and velocity are - so this can
// always be the same reused object regardless of whether `stateAt` itself was
// given an `out`.
const STATE_SCRATCH_ELEMENTS = newElements();

/**
 * State from one element set at a TDB Julian date. Heliocentric where the
 * elements are heliocentric, parent-relative where they are not: the caller
 * composes, `heliocentric` below does it.
 */
export function stateAt(orbit: Orbit, jdTdb: number, out?: MutableState): State {
  const o = out ?? newState();
  const el = elementsAt(orbit, jdTdb, STATE_SCRATCH_ELEMENTS);
  positionFromE(el, el.E, o.position);
  const cosE = Math.cos(el.E);
  const sinE = Math.sin(el.E);

  const joined = jointRatesAt(orbit, jdTdb);
  if (joined === null) {
    // The mean motion comes from the element set's own L rate, so the pair stays
    // self-consistent with no gravitational parameter of its own.
    const xpDot = -el.a * sinE * el.dEdt;
    const ypDot = el.a * el.flat * cosE * el.dEdt;
    o.velocity.x = (el.m11 * xpDot + el.m12 * ypDot) * AU_KM;
    o.velocity.y = (el.m21 * xpDot + el.m22 * ypDot) * AU_KM;
    o.velocity.z = (el.m31 * xpDot + el.m32 * ypDot) * AU_KM;
    return o;
  }

  // The joined orbit's a, e, i, node and peri each carry the Hermite join's own
  // rate (jointRatesAt), so the velocity is the full derivative of the
  // rotation and the orbital-plane position - not the plane swept at a fixed
  // a/e/i/node/peri the way a single Kepler set's own velocity is - or Saturn's
  // and the main belt's 365-day-sampled elements miss it by tens to hundreds
  // of m/s where node and peri move fastest.
  const { aDotAuPerSec, eDotPerSec, iDotRadPerSec, nodeDotRadPerSec, periDotRadPerSec } = joined;
  const dEdt = el.dEdt + (sinE * eDotPerSec - periDotRadPerSec) / (1 - el.e * cosE);
  const xp = el.a * (cosE - el.e);
  const yp = el.a * el.flat * sinE;
  const xpDot = aDotAuPerSec * (cosE - el.e) + el.a * (-sinE * dEdt - eDotPerSec);
  const ypDot = aDotAuPerSec * el.flat * sinE - el.a * (el.e * eDotPerSec / el.flat) * sinE + el.a * el.flat * cosE * dEdt;

  // The rotation matrix's own derivative: m11..m32 are built from cos/sin of
  // w (peri - node), node and i, so each is a sum of terms in wDot, the node
  // rate and the inclination rate - worked out by differentiating m11..m32's
  // own formulas above and regrouping, which is why the coefficients below
  // are other m-entries rather than fresh trig calls.
  const cw = Math.cos(el.wRad);
  const sw = Math.sin(el.wRad);
  const cn = Math.cos(el.nodeRad);
  const sn = Math.sin(el.nodeRad);
  const ci = Math.cos(el.iRad);
  const si = Math.sin(el.iRad);
  const wDot = periDotRadPerSec - nodeDotRadPerSec;
  const dm11 = el.m12 * wDot - el.m21 * nodeDotRadPerSec + sw * sn * si * iDotRadPerSec;
  const dm12 = -el.m11 * wDot - el.m22 * nodeDotRadPerSec + cw * sn * si * iDotRadPerSec;
  const dm21 = el.m22 * wDot + el.m11 * nodeDotRadPerSec - sw * cn * si * iDotRadPerSec;
  const dm22 = -el.m21 * wDot + el.m12 * nodeDotRadPerSec - cw * cn * si * iDotRadPerSec;
  const dm31 = el.m32 * wDot + sw * ci * iDotRadPerSec;
  const dm32 = -el.m31 * wDot + cw * ci * iDotRadPerSec;

  o.velocity.x = (el.m11 * xpDot + el.m12 * ypDot + dm11 * xp + dm12 * yp) * AU_KM;
  o.velocity.y = (el.m21 * xpDot + el.m22 * ypDot + dm21 * xp + dm22 * yp) * AU_KM;
  o.velocity.z = (el.m31 * xpDot + el.m32 * ypDot + dm31 * xp + dm32 * yp) * AU_KM;
  return o;
}

const MAX_PARENT_DEPTH = 8;

// Row id to row, built once per distinct `rows` array and cached on it, so
// `compose` stops walking the array with `.find` on every parent-relative
// body on every call. `rows` is the registry's own array, built once at load
// and never replaced in place, so keying on its identity is keying on load.
const PARENT_INDEX_CACHE = new WeakMap<readonly BodyRow[], Map<string, BodyRow>>();

function parentIndexFor(rows: readonly BodyRow[]): Map<string, BodyRow> {
  let index = PARENT_INDEX_CACHE.get(rows);
  if (index === undefined) {
    index = new Map();
    for (const r of rows) index.set(r.id, r);
    PARENT_INDEX_CACHE.set(rows, index);
  }
  return index;
}

// The one `own` state alive at a time while `composeInto` walks a parent
// chain: consumed into `out` before the next iteration overwrites it, so nothing
// ever reads it stale. Chains in the registry today are one level deep (a
// satellite onto its own heliocentric planet), so this walk and the original
// recursion below it sum in the same order and land bit for bit the same;
// only a chain two or more parents deep, which the registry does not carry,
// could differ, and then only by the rounding a different summation order
// implies.
const COMPOSE_SCRATCH_OWN = newState();
const PARTNER_SCRATCH = newState();
const PARTNER_JOINED = newState();

// The partner's state is read at half-day nodes and joined with a cubic
// Hermite: 0.32 km and 0.023 m/s at worst on the Moon, 3.9 m on Earth's step,
// where the lunar series itself costs 11 microseconds a call and a trip
// planner or a predictor asks for Earth at thousands of dates with no Moon beside it.
const PARTNER_NODE_DAYS = 0.5;
const PARTNER_NODE_CAP = 512;
const PARTNER_NODES = new WeakMap<Orbit, Map<number, Float64Array>>();

function partnerNode(orbit: Orbit, k: number): Float64Array {
  let nodes = PARTNER_NODES.get(orbit);
  if (nodes === undefined) {
    nodes = new Map();
    PARTNER_NODES.set(orbit, nodes);
  }
  let node = nodes.get(k);
  if (node === undefined) {
    if (nodes.size >= PARTNER_NODE_CAP) nodes.clear();
    const s = stateAt(orbit, k * PARTNER_NODE_DAYS, PARTNER_SCRATCH);
    node = Float64Array.of(s.position.x, s.position.y, s.position.z, s.velocity.x, s.velocity.y, s.velocity.z);
    nodes.set(k, node);
  }
  return node;
}

function partnerStateAt(orbit: Orbit, jdTdb: number, out: MutableState): State {
  const k = Math.floor(jdTdb / PARTNER_NODE_DAYS);
  const a = partnerNode(orbit, k);
  const b = partnerNode(orbit, k + 1);
  const s = jdTdb / PARTNER_NODE_DAYS - k;
  const s2 = s * s;
  const s3 = s2 * s;
  const span = PARTNER_NODE_DAYS * 86400;
  const h00 = 2 * s3 - 3 * s2 + 1;
  const h10 = (s3 - 2 * s2 + s) * span;
  const h01 = 3 * s2 - 2 * s3;
  const h11 = (s3 - s2) * span;
  const d00 = (6 * s2 - 6 * s) / span;
  const d10 = 3 * s2 - 4 * s + 1;
  const d01 = (6 * s - 6 * s2) / span;
  const d11 = 3 * s2 - 2 * s;
  out.position.x = h00 * a[0]! + h10 * a[3]! + h01 * b[0]! + h11 * b[3]!;
  out.position.y = h00 * a[1]! + h10 * a[4]! + h01 * b[1]! + h11 * b[4]!;
  out.position.z = h00 * a[2]! + h10 * a[5]! + h01 * b[2]! + h11 * b[5]!;
  out.velocity.x = d00 * a[0]! + d10 * a[3]! + d01 * b[0]! + d11 * b[3]!;
  out.velocity.y = d00 * a[1]! + d10 * a[4]! + d01 * b[1]! + d11 * b[4]!;
  out.velocity.z = d00 * a[2]! + d10 * a[5]! + d01 * b[2]! + d11 * b[5]!;
  return out;
}

/**
 * Adds to `out` the step from a row's element point to its own centre: zero
 * unless the elements are a barycentre (`orbit.barycentre_with`), and then
 * minus the partner's parent-relative state times the partner's share of the
 * pair's mass, which leaves the barycentre where the elements put it.
 */
function addBarycentreStep(row: BodyRow, index: Map<string, BodyRow>, jdTdb: number, out: MutableState): void {
  const partnerId = row.orbit?.barycentre_with;
  if (partnerId === undefined) return;
  const partner = index.get(partnerId);
  if (partner?.orbit == null) {
    throw new Error(`ephemeris: "${row.id}" is a barycentre with "${partnerId}", which is not an orbiting row`);
  }
  const share = partner.mass_kg / (row.mass_kg + partner.mass_kg);
  const s = partnerStateAt(partner.orbit, jdTdb, PARTNER_JOINED);
  out.position.x -= share * s.position.x;
  out.position.y -= share * s.position.y;
  out.position.z -= share * s.position.z;
  out.velocity.x -= share * s.velocity.x;
  out.velocity.y -= share * s.velocity.y;
  out.velocity.z -= share * s.velocity.z;
}

/**
 * Where a row's own centre stands from the point its elements describe: zero
 * for every row but one whose elements are a barycentre. The orrery carries
 * Earth's ring, which is the barycentre's ellipse, by this so it passes through Earth.
 */
export function barycentreStep(row: BodyRow, rows: readonly BodyRow[], jdTdb: number, out?: MutableState): State {
  const o = out ?? newState();
  o.position.x = 0; o.position.y = 0; o.position.z = 0;
  o.velocity.x = 0; o.velocity.y = 0; o.velocity.z = 0;
  addBarycentreStep(row, parentIndexFor(rows), jdTdb, o);
  return o;
}

function composeInto(
  row: BodyRow,
  rows: readonly BodyRow[],
  jdTdb: number,
  out: MutableState,
): State {
  out.position.x = 0; out.position.y = 0; out.position.z = 0;
  out.velocity.x = 0; out.velocity.y = 0; out.velocity.z = 0;
  const index = parentIndexFor(rows);
  let cur: BodyRow = row;
  let depth = 0;
  for (;;) {
    if (cur.orbit === null) return out;
    const own = stateAt(cur.orbit, jdTdb, COMPOSE_SCRATCH_OWN);
    addBarycentreStep(cur, index, jdTdb, COMPOSE_SCRATCH_OWN);
    out.position.x += own.position.x;
    out.position.y += own.position.y;
    out.position.z += own.position.z;
    out.velocity.x += own.velocity.x;
    out.velocity.y += own.velocity.y;
    out.velocity.z += own.velocity.z;
    if (!cur.orbit.parent_relative) return out;
    if (depth >= MAX_PARENT_DEPTH) {
      throw new Error(`ephemeris: parent chain from "${cur.id}" exceeds ${MAX_PARENT_DEPTH} bodies`);
    }
    const parentId = cur.parent;
    if (parentId === null) {
      throw new Error(`ephemeris: body "${cur.id}" has parent-relative elements and no parent`);
    }
    const parent = index.get(parentId);
    if (parent === undefined) {
      throw new Error(
        `ephemeris: body "${cur.id}" is relative to "${parentId}", which is not in the registry`,
      );
    }
    cur = parent;
    depth += 1;
  }
}

/**
 * Heliocentric ecliptic J2000 state of one registry row, composing a
 * parent-relative body onto its parent. The frame origin (no orbit) is at rest
 * at zero. A row whose elements are a barycentre is placed at its own centre,
 * so Earth is the geocentre and Luna composes onto it.
 */
export function heliocentric(
  row: BodyRow,
  rows: readonly BodyRow[],
  jdTdb: number,
  out?: MutableState,
): State {
  return composeInto(row, rows, jdTdb, out ?? newState());
}

export function distanceKm(v: Vec3): number {
  return Math.hypot(v.x, v.y, v.z);
}

// Separate from `COMPOSE_SCRATCH_OWN`: `composeInto` below overwrites that one
// on every step of the parent walk `heliocentricOrbit` itself triggers, so the
// ship's own state needs a scratch object composeInto never touches.
const ORBIT_OWN_SCRATCH = newState();

/**
 * Heliocentric ecliptic J2000 state of an orbit that is not a registry row -
 * a ship's own licensed orbit (`src/sim/orbit.ts`) - composed onto the row it
 * is relative to. Same composition `heliocentric` does, with the orbit handed
 * in rather than read off a row.
 */
export function heliocentricOrbit(
  orbit: Orbit,
  parentId: string | null,
  rows: readonly BodyRow[],
  jdTdb: number,
  out?: MutableState,
): State {
  const o = out ?? newState();
  const own = stateAt(orbit, jdTdb, ORBIT_OWN_SCRATCH);
  if (!orbit.parent_relative) {
    o.position.x = own.position.x; o.position.y = own.position.y; o.position.z = own.position.z;
    o.velocity.x = own.velocity.x; o.velocity.y = own.velocity.y; o.velocity.z = own.velocity.z;
    return o;
  }
  if (parentId === null) {
    throw new Error('ephemeris: a parent-relative orbit was composed with no parent');
  }
  const index = parentIndexFor(rows);
  const parent = index.get(parentId);
  if (parent === undefined) {
    throw new Error(`ephemeris: orbit is relative to "${parentId}", which is not in the registry`);
  }
  composeInto(parent, rows, jdTdb, o);
  o.position.x += own.position.x;
  o.position.y += own.position.y;
  o.position.z += own.position.z;
  o.velocity.x += own.velocity.x;
  o.velocity.y += own.velocity.y;
  o.velocity.z += own.velocity.z;
  return o;
}
